import * as React from 'react';
import Layout from '../../../components/Layout';
import { useUser } from '../../../lib/user';
import useSWR from 'swr';
import CsvExportRequestsTable from '../../../components/CsvExports/CsvExportRequestsTable';
import { CsvExportRequestList } from '@wooindex/common/types';
import { makeStyles, Container } from '@material-ui/core';
import UpgradePlanCtaMessage from '../../../components/UpgradePlanCta';
import createGetServerSideProps from 'lib/createGetServerSideProps';

const REFRESH_INTERVAL = 2000;

function calculateRefreshInterval (csvExportRequests?: CsvExportRequestList): number | undefined {
  if (csvExportRequests && csvExportRequests.items.some(request => request.status !== 'FINISHED')) {
    return REFRESH_INTERVAL;
  } else {
    return undefined;
  }
}

const useStyles = makeStyles(theme => ({
  emptyContainer: {
    padding: theme.spacing(2, 0),
    textAlign: 'center'
  },
  rowsLimitCta: {
    margin: theme.spacing(2, 0)
  },
  requestsTable: {
    margin: theme.spacing(6, 0)
  }
}));

function DashBoardContent () {
  const classes = useStyles();
  const { user } = useUser();
  const [refreshInterval, setRefreshInterval] = React.useState<number | undefined>();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const search = React.useMemo(() => new URLSearchParams({
    count: String(true),
    page: String(page),
    rowsPerPage: String(rowsPerPage)
  }), [page, rowsPerPage]);
  const {
    data: csvExportRequests,
    error,
    mutate
  } = useSWR<CsvExportRequestList>(user ? `/api/csv-exports?${search}` : null, {
    dedupingInterval: REFRESH_INTERVAL - 1,
    refreshInterval,
    onError: console.error
  });

  React.useEffect(() => {
    setRefreshInterval(calculateRefreshInterval(csvExportRequests));
  }, [csvExportRequests]);

  return (
    <div>
      {user
        ? (
          <UpgradePlanCtaMessage
            capabilities={user.capabilities}
            className={classes.rowsLimitCta}
          />
          )
        : null}
      {error
        ? (
          <div className={classes.emptyContainer}>
            Failed to load CSV exports data
          </div>
          )
        : (
          <CsvExportRequestsTable
            className={classes.requestsTable}
            csvExportRequests={csvExportRequests}
            loading={!csvExportRequests}
            refresh={mutate}
            page={page}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
          />
          )}
    </div>
  );
}

export const getServerSideProps = createGetServerSideProps();

export default function Dashboard () {
  return (
    <Layout active='exports' loginRequired>
      <Container maxWidth='md'>
        <DashBoardContent />
      </Container>
    </Layout>
  );
}
